import { FormattedMessage } from 'react-intl';
import {
  always,
  compose,
  ifElse,
  is,
  isEmpty,
  isNil,
  propOr,
  trim,
} from 'ramda';

import messages from '../App/messages';
import { N2W_LOCALE_MAP } from './constants';
import { MOVE_OUT_PRORATE_MEHTODS } from '../ResidentProfile/constants';
import { PRORATE_METHODS as DEPRECATED_METHODS } from '../../components/CompleteRenewalCheck/RenewalProrates/constants';

import type {
  ScreeningProviderPropertyProfile,
  ScreeningPropertyConfig,
  ScreeningSetupData,
  ScreeningSubmitPayload,
  ScreeningSetupDataEntry,
} from './types';

export const getBooleanDisplayValue = (value: boolean = false) => {
  return value ? (
    <FormattedMessage {...messages.on} />
  ) : (
    <FormattedMessage {...messages.off} />
  );
};

export const getN2WLocale = (locale: string = ''): string => {
  const subLocale = locale.substring(0, 2);

  return N2W_LOCALE_MAP[subLocale] ?? 'EN_US';
};

export const getEquivalentDeprecatedMoveOutProrateMethodId = (
  moveOutProrateMethodId,
  deprecatedProrateMethods,
  moveOutProrateMethods,
) => {
  const newYesOptions = [
    MOVE_OUT_PRORATE_MEHTODS.YES_FULL_MONTH,
    MOVE_OUT_PRORATE_MEHTODS.YES_MONTH_START,
  ];
  const deprecatedYesOption = deprecatedProrateMethods.find((method) => {
    return method.name === DEPRECATED_METHODS.YES_ALWAYS;
  });
  const deprecatedNoOption = deprecatedProrateMethods.find((method) => {
    return method.name === DEPRECATED_METHODS.NO_NEVER;
  });
  const method = moveOutProrateMethods.find((method) => {
    return method.id === moveOutProrateMethodId;
  });
  const methodName = method ? method.name : null;

  return newYesOptions.includes(methodName)
    ? deprecatedYesOption.id
    : deprecatedNoOption.id;
};

export const getEquivalentMoveOutProrateMethodId = (
  deprecated_moveOutProrateMethodId,
  deprecatedProrateMethods,
  moveOutProrateMethods,
) => {
  const newYesOption = moveOutProrateMethods.find(
    (method) => method.name === MOVE_OUT_PRORATE_MEHTODS.YES_FULL_MONTH,
  );
  const newNoOption = moveOutProrateMethods.find(
    (method) => method.name === MOVE_OUT_PRORATE_MEHTODS.NO_NEVER,
  );
  const deprecatedMethodName = deprecatedProrateMethods.find((method) => {
    return method.id === deprecated_moveOutProrateMethodId;
  }).name;

  return deprecatedMethodName === DEPRECATED_METHODS.YES_ALWAYS
    ? newYesOption.id
    : newNoOption.id;
};

export function getScreeningSetupData({
  providerPropertyProfiles = [],
  screeningPropertyConfigs = [],
  allProviders = [],
}: {
  providerPropertyProfiles?: Array<ScreeningProviderPropertyProfile>,
  screeningPropertyConfigs?: Array<ScreeningPropertyConfig>,
  allProviders?: Array<{ id: string, name: string, isActive: boolean }>,
}): ScreeningSetupData | void {
  if (!allProviders?.length && !providerPropertyProfiles?.length) {
    return undefined;
  }

  // Build a lookup of configured profiles by provider name
  const profilesByProviderName = (providerPropertyProfiles ?? []).reduce(
    (acc, profile) => {
      if (profile?.provider?.name) {
        acc[profile.provider.name] = profile;
      }
      return acc;
    },
    {},
  );

  // Start with all active providers and overlay profile data where it exists
  const providerNames = new Set([
    ...(allProviders ?? []).map((p) => p.name),
    ...Object.keys(profilesByProviderName),
  ]);

  return Array.from(providerNames).reduce(
    (data: ScreeningSetupData, providerName) => {
      const profile = profilesByProviderName[providerName];
      const allProvider = (allProviders ?? []).find(
        (p) => p.name === providerName,
      );
      const providerId = profile?.provider?.id ?? allProvider?.id ?? '';

      if (!profile) {
        // Provider exists in Fortress but has no config for this property
        return {
          ...data,
          [providerName]: {
            profileId: null,
            providerId,
            configId: null,
            providerName,
            isActive: false,
            isEnabled: false,
            order: 0,
            defaultProfile: undefined,
            hasConfig: false,
            screeningMethods: [],
          },
        };
      }

      // TODO: Remove this check once we have a way to determine if a defaultProfile code is sensitive or not
      // This check is also on fortress-backend. Should not even be included in response but leaving this here just
      // in case. This should be handled on screening service
      const config =
        profile.provider.name === 'TransUnion'
          ? screeningPropertyConfigs?.find(
              (config) =>
                config?.accountConfig?.provider?.id === profile.provider.id,
            )
          : null;
      const hasConfig = !isNil(config);

      return {
        ...data,
        [providerName]: {
          profileId: profile.id,
          providerId: profile.provider.id,
          configId: config?.id,
          providerName: profile.provider.name,
          isActive: profile.isActive,
          isEnabled: profile.isEnabled,
          order: profile.order,
          defaultProfile: config?.defaultProfile,
          hasConfig,
          screeningMethods: profile.screeningMethods ?? [],
        },
      };
    },
    {},
  );
}

export function hasOverlappingScreeningMethods(
  entry: ScreeningSetupDataEntry,
  activeProviders: ScreeningSetupDataEntry[],
): boolean {
  if (!entry.screeningMethods?.length) {
    return false;
  }
  const entryMethodSet = new Set(entry.screeningMethods);
  return activeProviders.some((active) =>
    (active.screeningMethods ?? []).some((method) =>
      entryMethodSet.has(method),
    ),
  );
}

export function mapScreeningSetupDataToSubmitPayload(
  data: ScreeningSetupData = {},
): ScreeningSubmitPayload | {} {
  const values = Object.values(data).filter(
    (entry: ScreeningSetupDataEntry) => !!entry.profileId,
  );

  return values.reduce(
    (
      { providerPropertyProfiles = [], screeningPropertyConfigs = [] },
      data,
    ) => ({
      providerPropertyProfiles: [
        ...providerPropertyProfiles,
        {
          id: data.profileId,
          isActive: data.isActive,
          order: data.order,
        },
      ],
      screeningPropertyConfigs:
        data.configId && !validateDefaultProfile(data) // only include config if it has a defaultProfile
          ? [
              ...screeningPropertyConfigs,
              {
                id: data.configId,
                defaultProfile: data.defaultProfile,
              },
            ]
          : screeningPropertyConfigs,
    }),
    {},
  );
}

// Returns true when invalid, false when valid
export function validateDefaultProfile(data: ScreeningSetupDataEntry): boolean {
  if (!data) {
    return true;
  }

  return (
    data.hasConfig &&
    (isNil(data.defaultProfile) ||
      isEmpty(data.defaultProfile) ||
      (data.defaultProfile.trim?.().length ?? 0) === 0)
  );
}

// Returns true when invalid, false when valid
export function validateScreeningSetupDataEntry(
  data: ScreeningSetupDataEntry,
): boolean {
  if (!data) {
    return true;
  }

  return validateDefaultProfile(data);
}

// Returns true when invalid, false when valid
export function validateScreeningSetupData(data: ScreeningSetupData): boolean {
  if (isNil(data)) {
    return true;
  }

  const activeEntries = Object.values(data).filter(
    (entry: ScreeningSetupDataEntry) => entry.isActive,
  );
  const hasConflicts = activeEntries.some((entry, i) =>
    hasOverlappingScreeningMethods(
      entry,
      activeEntries.filter((_, j) => j !== i),
    ),
  );
  if (hasConflicts) {
    return true;
  }

  return Object.values(data).some((entry: ScreeningSetupDataEntry) => {
    return validateScreeningSetupDataEntry(entry);
  });
}

export const camelCaseToWords = (str: string) =>
  str.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) {
    return str.toUpperCase();
  });

export const cleanData = (data: Object, property: string) =>
  compose(ifElse(is(String), trim, always('')), propOr('', property))(data);

export const numberInRange = (num: number, min: number, max: number) =>
  num >= min && num <= max;
